import React, { useMemo, useState } from "react";
import { Alert, Button, Card, Collapse, Empty, Form, Input, List, Modal, Skeleton, Space, Table, Tag, Typography } from "antd";
import dayjs from "dayjs";
import { useMemberService } from "../../../services/members/MemberServiceProvider";
import { HealthAnswer, HealthFormReview, HealthFormSummary } from "../../../model/members/HealthFormSummary";
import { QuestionnaireField, QuestionnaireSchema } from "../forms/QuestionnaireSchema";
import { computeFlaggedAnswers } from "../forms/healthFlags";
import { schemaToAnswers, answersToFormValues } from "../forms/schemaToAnswers";
import DynamicQuestionnaire from "../forms/DynamicQuestionnaire";
import { useFormatDateTime } from "../../../hooks/useFormatters";

// Date + time + club timezone abbreviation (e.g. "07 Jun 2026 14:30 BST").
const DATE_TIME_TZ = 'DD MMM YYYY HH:mm z';

const { Text, Paragraph } = Typography;

interface Props {
    membershipId: string;
    schema: QuestionnaireSchema;
    summary: HealthFormSummary | null;
    loading: boolean;
    // Staff may record a review (who/when + notes). A member viewing their own
    // profile sees reviews read-only and never gets the review controls.
    canReview: boolean;
    // A member submits their own questionnaire via the /me endpoint; staff submit
    // against a member by id.
    selfServiceMode?: boolean;
    onHealthSummaryChanged: () => void;
    backgroundColor?: string;
    borderColor?: string;
}

// Member-profile health tab. Renders the latest questionnaire answers against the
// client's schema, flags answers that need attention (e.g. a heart condition),
// and lists the full submission history. Staff can additionally record that the
// answers were reviewed (who/when audit); a member sees those review notes
// read-only. Both staff and the member themselves can complete/update the
// questionnaire via the editor modal.
export const HealthResultsTab: React.FC<Props> = ({
    membershipId,
    schema,
    summary,
    loading,
    canReview,
    selfServiceMode = false,
    onHealthSummaryChanged,
    backgroundColor = '#ffffff',
    borderColor = '#e5e7eb',
}) => {
    const memberService = useMemberService();
    const formatDateTime = useFormatDateTime();
    const [notes, setNotes] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const [editorForm] = Form.useForm();
    const [editorOpen, setEditorOpen] = useState(false);
    const [saving, setSaving] = useState(false);

    const fields = useMemo(() => {
        const map = new Map<string, QuestionnaireField>();
        for (const section of schema.sections) for (const f of section.fields) map.set(f.key, f);
        return map;
    }, [schema]);

    const flagged = useMemo(
        () => (summary ? computeFlaggedAnswers(schema, summary.answers) : []),
        [schema, summary],
    );

    const hasSubmission = !!summary && summary.submissionId !== null;

    // Display the stored answer using the field's option labels where available.
    const formatAnswer = (key: string, raw: string): string => {
        const field = fields.get(key);
        if (!field) return raw;
        if (field.type === 'boolean') return raw === 'true' ? 'Yes' : 'No';
        const labelFor = (v: string) => field.options?.find((o) => o.value === v)?.label ?? v;
        if (field.type === 'checkboxGroup') return raw.split('|').filter(Boolean).map(labelFor).join(', ');
        if (field.type === 'radio' || field.type === 'select') return labelFor(raw);
        if (field.type === 'date') return raw ? dayjs(raw).format('DD MMM YYYY') : '';
        return raw;
    };

    // Answers table for a set of answers, flagging the ones that need attention.
    // Flags are computed against THIS answer set (not the latest submission), so a
    // historical submission shows the flags it actually had at the time.
    const answersTable = (answers: HealthAnswer[]) => {
        const keys = new Set(computeFlaggedAnswers(schema, answers).map((f) => f.key));
        const rows = answers.map((a) => ({
            key: a.question,
            label: fields.get(a.question)?.label ?? a.question,
            answer: formatAnswer(a.question, a.answer),
            flagged: keys.has(a.question),
        }));
        return (
            <Table
                size="small"
                pagination={false}
                rowKey="key"
                dataSource={rows}
                columns={[
                    {
                        title: 'Question',
                        dataIndex: 'label',
                        render: (label: string, row) => (
                            <span>
                                {label} {row.flagged && <Tag color="red">flag</Tag>}
                            </span>
                        ),
                    },
                    { title: 'Answer', dataIndex: 'answer' },
                ]}
            />
        );
    };

    // Read-only review history list, shared by the review card and each history
    // entry. Renders nothing when there are no reviews.
    const reviewList = (reviews: HealthFormReview[], header?: React.ReactNode) =>
        reviews.length > 0 ? (
            <List
                style={{ marginTop: header ? 16 : 0 }}
                size="small"
                header={header}
                dataSource={reviews}
                renderItem={(r) => (
                    <List.Item data-testid="health-tab-review-item">
                        <Space direction="vertical" size={0}>
                            <Text>
                                {formatDateTime(r.reviewedAt, DATE_TIME_TZ)} · {r.reviewedBy}
                            </Text>
                            {r.notes && <Text type="secondary">{r.notes}</Text>}
                        </Space>
                    </List.Item>
                )}
            />
        ) : null;

    // Open the questionnaire editor — pre-filled from the latest submission when
    // updating, blank when completing for the first time.
    const openEditor = () => {
        if (hasSubmission && summary) {
            editorForm.setFieldsValue(answersToFormValues(schema, summary.answers));
        } else {
            editorForm.resetFields();
        }
        setEditorOpen(true);
    };

    const handleSaveQuestionnaire = async () => {
        let values: Record<string, unknown>;
        try {
            values = await editorForm.validateFields();
        } catch {
            return; // validation errors are shown inline; keep the modal open
        }
        setSaving(true);
        try {
            const answers = schemaToAnswers(schema, values);
            // A member submits against their own membership via the /me endpoint;
            // staff submit against the loaded member by id.
            if (selfServiceMode) {
                await memberService.submitMyHealthForm(answers);
            } else {
                await memberService.submitHealthForm(membershipId, answers);
            }
            setEditorOpen(false);
            onHealthSummaryChanged(); // reload the summary so the new submission shows
        } finally {
            setSaving(false);
        }
    };

    const editor = (
        <Modal
            title={hasSubmission ? 'Update health questionnaire' : 'Complete health questionnaire'}
            open={editorOpen}
            onCancel={() => setEditorOpen(false)}
            width={720}
            data-testid="health-tab-editor"
            footer={[
                <Button key="cancel" onClick={() => setEditorOpen(false)}>Cancel</Button>,
                <Button
                    key="save"
                    type="primary"
                    data-testid="health-tab-editor-save"
                    loading={saving}
                    onClick={handleSaveQuestionnaire}
                >
                    Save questionnaire
                </Button>,
            ]}
        >
            <DynamicQuestionnaire schema={schema} form={editorForm} />
        </Modal>
    );

    if (loading) return <Card style={{ backgroundColor, borderColor }}><Skeleton active /></Card>;

    if (!hasSubmission) {
        return (
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                <Card style={{ backgroundColor, borderColor }} data-testid="health-tab-empty">
                    <Empty
                        description={
                            selfServiceMode
                                ? "You haven't completed your health questionnaire yet."
                                : "No health questionnaire on file for this member."
                        }
                    >
                        <Button
                            type="primary"
                            data-testid="health-tab-complete-btn"
                            onClick={openEditor}
                        >
                            Complete health questionnaire
                        </Button>
                    </Empty>
                </Card>
                {editor}
            </Space>
        );
    }

    const reviewed = summary!.reviews.length > 0;
    const latestReview = summary!.reviews[0];
    // The history includes the latest submission; only surface the older ones in
    // the history section (the latest is already shown in the answers card).
    const priorSubmissions = (summary!.history ?? []).filter((h) => h.submissionId !== summary!.submissionId);

    const handleReview = async () => {
        setSubmitting(true);
        try {
            await memberService.recordHealthReview(membershipId, notes.trim() || undefined);
            setNotes('');
            onHealthSummaryChanged();
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            {flagged.length > 0 && !reviewed && canReview && (
                <Alert
                    type="error"
                    showIcon
                    data-testid="health-tab-flag-alert"
                    message="Flagged health answers — review required"
                    description={
                        <span>
                            The member reported: {flagged.map((f) => f.label).join('; ')}. Please check with the
                            member and mark the questionnaire as reviewed.
                        </span>
                    }
                />
            )}
            {flagged.length > 0 && !reviewed && !canReview && (
                <Alert
                    type="info"
                    showIcon
                    data-testid="health-tab-flag-alert-member"
                    message="Thanks for completing your health questionnaire"
                    description="Some of your answers may need a quick chat with the club before you exercise. A member of staff will be in touch."
                />
            )}
            {flagged.length > 0 && reviewed && (
                <Alert
                    type="success"
                    showIcon
                    data-testid="health-tab-reviewed-alert"
                    message={`Flagged answers reviewed by ${latestReview.reviewedBy} on ${formatDateTime(latestReview.reviewedAt, DATE_TIME_TZ)}.`}
                />
            )}

            <Card
                title={`Health questionnaire — submitted ${summary!.submittedAt ? formatDateTime(summary!.submittedAt, DATE_TIME_TZ) : ''}`}
                style={{ backgroundColor, borderColor }}
                data-testid="health-tab-answers"
                extra={
                    <Button data-testid="health-tab-update-btn" onClick={openEditor}>
                        Update questionnaire
                    </Button>
                }
            >
                {answersTable(summary!.answers)}
            </Card>

            <Card title="Review" style={{ backgroundColor, borderColor }} data-testid="health-tab-review">
                {canReview ? (
                    <>
                        {!reviewed && (
                            <Paragraph type="secondary">
                                Confirm you have checked any flagged answers with the member, then record the review.
                            </Paragraph>
                        )}
                        <Space direction="vertical" style={{ width: '100%' }}>
                            <Input.TextArea
                                data-testid="health-tab-review-notes"
                                rows={2}
                                placeholder="Optional notes (e.g. cleared to exercise, GP letter on file)"
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                            />
                            <Button
                                type="primary"
                                data-testid="health-tab-review-btn"
                                loading={submitting}
                                onClick={handleReview}
                            >
                                Mark as reviewed
                            </Button>
                        </Space>
                        {reviewList(summary!.reviews, <Text strong>Review history</Text>)}
                    </>
                ) : reviewed ? (
                    // Members see the review audit read-only — they can never edit it.
                    reviewList(summary!.reviews, <Text strong>Reviewed by the club</Text>)
                ) : (
                    <Paragraph type="secondary" data-testid="health-tab-review-pending">
                        Your questionnaire has not been reviewed by the club yet.
                    </Paragraph>
                )}
            </Card>

            {priorSubmissions.length > 0 && (
                <Card title="Submission history" style={{ backgroundColor, borderColor }} data-testid="health-tab-history">
                    <Collapse
                        items={priorSubmissions.map((h) => ({
                            key: String(h.submissionId),
                            label: `Submitted ${formatDateTime(h.submittedAt, DATE_TIME_TZ)}`,
                            children: (
                                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                                    {answersTable(h.answers)}
                                    {reviewList(h.reviews, <Text strong>Reviews</Text>)}
                                </Space>
                            ),
                        }))}
                    />
                </Card>
            )}
            {editor}
        </Space>
    );
};

export default HealthResultsTab;
